import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { CapitalGainsAssetClass } from "@xynex-money/shared";
import type { CapitalGainsTerm } from "@xynex-money/shared";
import { api, ApiError } from "../api";
import { useCrudResource } from "../hooks/useCrudResource";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { MoneyInput } from "../components/MoneyInput";
import { DateInput } from "../components/DateInput";
import { fmtDate, fmtINR } from "../fmt";

const ASSET_CLASSES = CapitalGainsAssetClass.options;

interface CapGain {
  id: string;
  assetDesc: string;
  assetClass: CapitalGainsAssetClass;
  buyDate: string;
  buyValue: number;
  sellDate: string;
  sellValue: number;
  expenses: number | null;
  useIndexation: boolean;
  gain: number;
  term: CapitalGainsTerm;
  notes: string | null;
}

interface FormState {
  assetDesc: string;
  assetClass: CapitalGainsAssetClass;
  buyDate: string;
  buyValue: number | null;
  sellDate: string;
  sellValue: number | null;
  expenses: number | null;
  useIndexation: boolean;
  notes: string;
}

const emptyForm: FormState = {
  assetDesc: "",
  assetClass: "equity",
  buyDate: "",
  buyValue: null,
  sellDate: "",
  sellValue: null,
  expenses: null,
  useIndexation: false,
  notes: "",
};

interface ParsedRow {
  assetDesc: string;
  assetClass: CapitalGainsAssetClass;
  buyDate: string;
  buyValue: number;
  sellDate: string;
  sellValue: number;
  expenses: number;
  gain: number;
  term: CapitalGainsTerm;
}

interface SkippedRow {
  rowNumber: number;
  reason: string;
  raw: string;
  sheet?: string;
}

interface ImportPreview {
  rows: ParsedRow[];
  skipped: SkippedRow[];
  detected: Record<string, string | null>;
  headerRowNumber: number | null;
}

export function CapGains() {
  const { items: rows, loading, error, setError, reload, create, update, remove } =
    useCrudResource<CapGain>("/api/capgains", "capital gains");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CapGain | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  // --- broker Excel import ---
  const fileRef = useRef<HTMLInputElement>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importClass, setImportClass] = useState<CapGain["assetClass"]>("equity");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const openImport = () => {
    setImportClass("equity");
    setImportFile(null);
    setPreview(null);
    setImportError(null);
    setImportOpen(true);
  };

  const parseImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    setImportError(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("defaultAssetClass", importClass);
      const res = await api.post<ImportPreview>("/api/capgains/import", fd);
      setPreview(res);
    } catch (err) {
      setImportError(err instanceof ApiError ? err.message : "could not parse file");
    } finally {
      setImportBusy(false);
    }
  };

  const commitImport = async () => {
    if (!preview || preview.rows.length === 0) return;
    setImportBusy(true);
    setImportError(null);
    try {
      const items = preview.rows.map((r) => ({
        assetDesc: r.assetDesc,
        assetClass: r.assetClass,
        buyDate: r.buyDate,
        buyValue: r.buyValue,
        sellDate: r.sellDate,
        sellValue: r.sellValue,
        expenses: r.expenses,
        useIndexation: false,
        notes: null,
      }));
      await api.post("/api/capgains/bulk", { items });
      setImportOpen(false);
      reload();
    } catch (err) {
      setImportError(err instanceof ApiError ? err.message : "import failed");
    } finally {
      setImportBusy(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (c: CapGain) => {
    setEditing(c);
    setForm({
      assetDesc: c.assetDesc,
      assetClass: c.assetClass,
      buyDate: c.buyDate,
      buyValue: c.buyValue,
      sellDate: c.sellDate,
      sellValue: c.sellValue,
      expenses: c.expenses,
      useIndexation: c.useIndexation,
      notes: c.notes ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      assetDesc: form.assetDesc,
      assetClass: form.assetClass,
      buyDate: form.buyDate,
      buyValue: form.buyValue ?? 0,
      sellDate: form.sellDate,
      sellValue: form.sellValue ?? 0,
      expenses: form.expenses ?? 0,
      useIndexation: form.useIndexation,
      notes: form.notes || null,
    };
    try {
      if (editing) await update(editing.id, body);
      else await create(body);
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (c: CapGain) => {
    if (!confirm(`Delete "${c.assetDesc}"?`)) return;
    try {
      await remove(c.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const columns: Column<CapGain>[] = [
    { key: "assetDesc", header: "Asset" },
    { key: "assetClass", header: "Class" },
    { key: "buyDate", header: "Buy date", render: (c) => fmtDate(c.buyDate) },
    { key: "sellDate", header: "Sell date", render: (c) => fmtDate(c.sellDate) },
    { key: "gain", header: "Gain", align: "right", render: (c) => fmtINR(c.gain) },
    { key: "term", header: "Term" },
    {
      key: "actions",
      header: "",
      render: (c) => (
        <span className="actions-inline">
          <button type="button" onClick={() => openEdit(c)}>
            Edit
          </button>
          <button type="button" className="danger" onClick={() => onDelete(c)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h1>Capital Gains</h1>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>
          Add capital gain
        </button>
        <button type="button" onClick={openImport}>
          Import broker Excel
        </button>
      </div>
      <Table columns={columns} rows={rows} rowKey={(c) => c.id} emptyText={loading ? "Loading…" : "No capital gains recorded yet."} />

      {modalOpen && (
        <Modal title={editing ? "Edit capital gain" : "Add capital gain"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="field">
              <label htmlFor="cg-desc">Asset description</label>
              <input id="cg-desc" required value={form.assetDesc} onChange={(e) => setForm({ ...form, assetDesc: e.target.value })} />
            </div>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="cg-class">Asset class</label>
                <select id="cg-class" value={form.assetClass} onChange={(e) => setForm({ ...form, assetClass: e.target.value as FormState["assetClass"] })}>
                  {ASSET_CLASSES.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <input
                    type="checkbox"
                    checked={form.useIndexation}
                    onChange={(e) => setForm({ ...form, useIndexation: e.target.checked })}
                  />
                  Use indexation
                </label>
              </div>
              <div className="field">
                <label htmlFor="cg-buydate">Buy date</label>
                <DateInput value={form.buyDate} onChange={(v) => setForm({ ...form, buyDate: v })} required />
              </div>
              <div className="field">
                <label htmlFor="cg-buyval">Buy value</label>
                <MoneyInput value={form.buyValue} onChange={(v) => setForm({ ...form, buyValue: v })} required />
              </div>
              <div className="field">
                <label htmlFor="cg-selldate">Sell date</label>
                <DateInput value={form.sellDate} onChange={(v) => setForm({ ...form, sellDate: v })} required />
              </div>
              <div className="field">
                <label htmlFor="cg-sellval">Sell value</label>
                <MoneyInput value={form.sellValue} onChange={(v) => setForm({ ...form, sellValue: v })} required />
              </div>
              <div className="field">
                <label htmlFor="cg-expenses">Expenses</label>
                <MoneyInput value={form.expenses} onChange={(v) => setForm({ ...form, expenses: v })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="cg-notes">Notes</label>
              <textarea id="cg-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {editing && (
              <div className="muted">
                Computed gain: {fmtINR(editing.gain)} ({editing.term} term)
              </div>
            )}
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {importOpen && (
        <Modal title="Import capital gains from broker Excel" onClose={() => setImportOpen(false)}>
          <div className="modal-body">
            <p className="muted">
              Upload the capital-gains statement (.xlsx / .csv) from your broker or
              registrar (Zerodha, Groww, ICICI Direct, CAMS, KFin…). Columns are
              auto-detected; review the preview before importing.
            </p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="imp-class">Default asset class</label>
                <select
                  id="imp-class"
                  value={importClass}
                  onChange={(e) => setImportClass(e.target.value as CapGain["assetClass"])}
                >
                  {ASSET_CLASSES.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <span className="muted">Used for rows whose class isn't in the file.</span>
              </div>
              <div className="field">
                <label htmlFor="imp-file">Statement file</label>
                <input
                  id="imp-file"
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={(e) => {
                    setImportFile(e.target.files?.[0] ?? null);
                    setPreview(null);
                  }}
                />
              </div>
            </div>

            {!preview && (
              <div className="modal-actions">
                <button type="button" onClick={() => setImportOpen(false)}>
                  Cancel
                </button>
                <button
                  className="primary"
                  type="button"
                  disabled={!importFile || importBusy}
                  onClick={parseImport}
                >
                  {importBusy ? "Parsing…" : "Parse file"}
                </button>
              </div>
            )}

            {preview && (
              <>
                <div className="muted">
                  {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"} parsed
                  {preview.headerRowNumber ? ` (header at row ${preview.headerRowNumber})` : ""}.
                  {preview.skipped.length > 0 && ` ${preview.skipped.length} skipped.`}
                </div>
                <div style={{ maxHeight: "40vh", overflow: "auto" }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Asset</th>
                        <th>Class</th>
                        <th>Buy date</th>
                        <th>Sell date</th>
                        <th style={{ textAlign: "right" }}>Buy ₹</th>
                        <th style={{ textAlign: "right" }}>Sell ₹</th>
                        <th style={{ textAlign: "right" }}>Gain</th>
                        <th>Term</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, i) => (
                        <tr key={i}>
                          <td>{r.assetDesc}</td>
                          <td>{r.assetClass}</td>
                          <td>{fmtDate(r.buyDate)}</td>
                          <td>{fmtDate(r.sellDate)}</td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.buyValue)}</td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.sellValue)}</td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.gain)}</td>
                          <td>{r.term}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.skipped.length > 0 && (
                  <details>
                    <summary className="muted">{preview.skipped.length} skipped rows</summary>
                    <ul className="muted" style={{ maxHeight: "20vh", overflow: "auto" }}>
                      {preview.skipped.map((s, i) => (
                        <li key={i}>
                          {s.sheet ? `[${s.sheet}] ` : ""}Row {s.rowNumber}: {s.reason} —{" "}
                          <code>{s.raw}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                <div className="modal-actions">
                  <button type="button" onClick={() => setPreview(null)} disabled={importBusy}>
                    Back
                  </button>
                  <button
                    className="primary"
                    type="button"
                    disabled={preview.rows.length === 0 || importBusy}
                    onClick={commitImport}
                  >
                    {importBusy ? "Importing…" : `Import ${preview.rows.length} rows`}
                  </button>
                </div>
              </>
            )}

            {importError && <div className="error-text">{importError}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}
